import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, MasteryHeatmap, Panel, ReadinessMeter } from '@usacow/ds';
import { api, type HomeData } from '../lib/api';

type Division = 'bronze' | 'silver' | 'gold' | 'platinum';

const RECO_CTA: Record<string, { label: string; to: string }> = {
  due_review: { label: 'Open review queue', to: '/app/review' },
  misconception_repair: { label: 'Start repair', to: '/app/repair' },
  prerequisite_gap: { label: 'Fill the gap', to: '/app/library' },
  active_topic_drill: { label: 'Continue', to: '/app/workout' },
  contest_simulation: { label: 'Start a mock', to: '/app/contest' },
  stretch_transfer: { label: 'Try a transfer problem', to: '/app/problem' },
};

export function Home() {
  const navigate = useNavigate();
  const [data, setData] = useState<HomeData | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .getHome()
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error)
    return <div className="route-loading">Couldn&apos;t load your home. Refresh to retry.</div>;
  if (!data) return <div className="route-loading">Loading…</div>;

  // First run — brand-new account, pre-diagnostic.
  if (data.firstRun) {
    return (
      <div className="home-empty">
        <div className="t-eyebrow">welcome</div>
        <h1 className="home-empty__h">Start with the diagnostic.</h1>
        <p className="home-empty__p">
          Fourteen minutes to find your reasoning bottlenecks. Everything after is targeted at what
          you actually need — no solved-count grinding.
        </p>
        <Button variant="primary" size="lg" onClick={() => navigate('/app/diagnostic')}>
          Take the diagnostic
        </Button>
      </div>
    );
  }

  const top = data.recommendation?.[0];
  const cta = top ? (RECO_CTA[top.kind] ?? RECO_CTA.stretch_transfer!) : null;
  const division = (data.division as Division) ?? 'silver';

  return (
    <div className="home">
      <section className="home-next">
        <div className="t-eyebrow">recommended now</div>
        <h1 className="home-next__title">{top?.reason ?? 'Keep training your weakest skill.'}</h1>
        <div className="home-next__cta">
          {cta && (
            <Link to={cta.to}>
              <Button variant="primary" size="lg">
                {cta.label}
              </Button>
            </Link>
          )}
        </div>
      </section>

      <div className="home-grid">
        <Panel
          title="Contest readiness"
          actions={
            <Badge tone={division} dot>
              {division[0]!.toUpperCase() + division.slice(1)}
            </Badge>
          }
        >
          <ReadinessMeter
            division={division}
            score={data.readiness?.score ?? 0}
            threshold={80}
            style={{ width: '100%' }}
          />
        </Panel>

        <Panel
          title="Review due"
          actions={
            <Button variant="ghost" size="sm" onClick={() => navigate('/app/review')}>
              Open
            </Button>
          }
        >
          <div className="home-review">
            <div className="home-review__big">{data.dueCount ?? 0}</div>
            <p className="home-card__note">Spaced reviews keep patterns from fading.</p>
          </div>
        </Panel>

        <Panel
          title="Topic mastery"
          meta="red dot = retention risk"
          actions={
            <Button variant="ghost" size="sm" onClick={() => navigate('/app/mastery')}>
              Map
            </Button>
          }
          style={{ gridColumn: '1 / -1' }}
        >
          <MasteryHeatmap
            cells={data.heatmap ?? []}
            columns={Math.min(12, Math.max(4, data.heatmap?.length ?? 4))}
            cellSize={22}
            onCellClick={() => navigate('/app/mastery')}
          />
        </Panel>
      </div>
    </div>
  );
}
